import { PATH_URL } from './path'
import { preserveUrlParams } from './preserveUrlParams'

/**
 * Opt-in date-first quiz entry for the "Check My Date" experiment (REV-417).
 *
 * Landing pages append `?start=date`. With no `start` param the quiz keeps
 * today's palette-first order and does not add analytics properties.
 *
 * Analytics value: `quiz_entry=date_first` on the variant. The control arm is
 * the existing events with `quiz_entry` omitted (`palette_first`).
 */
export const QUIZ_START_PARAM = 'start'
export const QUIZ_START_DATE_VALUE = 'date'
export const QUIZ_ENTRY_DATE_FIRST = 'date_first'
export const QUIZ_ENTRY_PALETTE_FIRST = 'palette_first'

const MONTHS = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
]

export const DATE_FIRST_FLOW_PATH = [
  PATH_URL.NEW_AVAILABILITY_PAGE,
  PATH_URL.NEW_COLOR_PALETTE_PAGE,
  PATH_URL.YOUR_DETAILS,
  PATH_URL.PLANNING_STAGE,
  PATH_URL.GUEST_COUNT,
  PATH_URL.NEW_BUDGET_PAGE,
  PATH_URL.ESSENTIALS_COLLECTION_PAGE,
  PATH_URL.CONTACT_INFO,
]

type HistoryLike = {
  push: (location: string | { pathname: string; search?: string }) => void
}

const searchString = (search: string) => (search.startsWith('?') ? search.slice(1) : search)

export const isDateFirstEntry = (search?: string): boolean => {
  const source = search !== undefined ? search : typeof window === 'undefined' ? '' : window.location.search
  return new URLSearchParams(searchString(source)).get(QUIZ_START_PARAM) === QUIZ_START_DATE_VALUE
}

export const quizEntryValue = (search?: string): typeof QUIZ_ENTRY_DATE_FIRST | typeof QUIZ_ENTRY_PALETTE_FIRST =>
  isDateFirstEntry(search) ? QUIZ_ENTRY_DATE_FIRST : QUIZ_ENTRY_PALETTE_FIRST

/**
 * Copy `quiz_entry` onto an analytics payload only for the date-first variant.
 * The default path gets the original object back, including `undefined`.
 */
export const withQuizEntry = <T>(properties: T, search?: string): T => {
  if (!isDateFirstEntry(search)) return properties
  const base = properties && typeof properties === 'object' ? (properties as Record<string, unknown>) : {}
  return ({ ...base, quiz_entry: QUIZ_ENTRY_DATE_FIRST } as unknown) as T
}

export const initialQuizPath = (search = ''): string =>
  isDateFirstEntry(search) ? PATH_URL.NEW_AVAILABILITY_PAGE : PATH_URL.NEW_COLOR_PALETTE_PAGE

const appendSearch = (pathname: string, search: string): string => {
  const params = new URLSearchParams(searchString(search))
  const url = new URL(pathname, 'http://localhost')
  params.forEach((value, key) => {
    url.searchParams.set(key, value)
  })
  return url.pathname + url.search
}

/**
 * Date-first navigations keep the current query string (so `start=date`,
 * serviceability, and prefill params survive). The default flow keeps the
 * existing pathname-only push.
 */
export const pushQuizStep = (history: HistoryLike, pathname: string, search: string) => {
  if (!isDateFirstEntry(search)) {
    history.push({ pathname })
    return
  }
  const windowSearch = typeof window !== 'undefined' ? window.location.search : ''
  history.push(windowSearch ? preserveUrlParams(pathname) : appendSearch(pathname, search))
}

export const formatMonthDay = (isoDate: string): string | null => {
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec((isoDate || '').trim())
  if (!match) return null
  const year = Number(match[1])
  const monthIndex = Number(match[2]) - 1
  const day = Number(match[3])
  const date = new Date(year, monthIndex, day)
  if (date.getFullYear() !== year || date.getMonth() !== monthIndex || date.getDate() !== day) return null
  return `${MONTHS[monthIndex]} ${day}`
}

export const cityLabelFromVenue = (venue?: { city?: string; place?: string }): string => {
  const city = venue?.city?.trim()
  if (city) return city
  const parts = (venue?.place || '')
    .split(',')
    .map((part) => part.trim())
    .filter(Boolean)
  if (!parts.length) return ''
  if (parts.length >= 3) {
    const stateToken = parts[parts.length - 1].replace(/\d/g, '').trim()
    if (/^[A-Za-z]{2}$/.test(stateToken)) return parts[parts.length - 2]
  }
  return parts[0]
}

export const formatAvailabilityPayoff = (city: string, isoDate: string): string | null => {
  const place = city.trim()
  const when = formatMonthDay(isoDate)
  if (!place || !when) return null
  return `Great news, we're available in ${place} on ${when}!`
}

const eventDateValue = (values: unknown): string => {
  if (Array.isArray(values)) return String(values[0] || '')
  return typeof values === 'string' ? values : ''
}

export const readAvailabilityPayoff = (): string | null => {
  try {
    const data = JSON.parse(localStorage.getItem('tempPoppySessionData') || '{}')
    const venues = Array.isArray(data.eventVenues) ? data.eventVenues : []
    const ceremony = venues.find((venue: { type?: string }) => venue?.type === 'CEREMONY') || venues[0]
    const responses = Array.isArray(data.quizResponses) ? data.quizResponses : []
    const eventDate = responses.find((response: { key?: string }) => response?.key === 'EVENT_DATE')
    return formatAvailabilityPayoff(cityLabelFromVenue(ceremony), eventDateValue(eventDate?.values))
  } catch (error) {
    return null
  }
}
